import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, FilePlus2, Eye, RefreshCw, FolderOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TaskList } from "@/components/TaskList";
import { TaskFormDialog } from "@/components/TaskFormDialog";
import { CompleteDialog } from "@/components/CompleteDialog";
import { Filters } from "@/components/Filters";
import { Pill } from "@/components/Badges";
import { api, formatDate } from "@/lib/api";
import { openDocument } from "@/lib/openDocument";
import { useQuickCreate } from "@/context/QuickCreate";
import { cn } from "@/lib/utils";

export default function SectionPage({ section }) {
  const [tasks, setTasks] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filesLoading, setFilesLoading] = useState(false);
  const [driveReady, setDriveReady] = useState(false);
  const [filters, setFilters] = useState({ status: "all", priority: "all", assignee: "all", range: "all" });
  const [formOpen, setFormOpen] = useState(false);
  const [initial, setInitial] = useState(null);
  const [completing, setCompleting] = useState(null);
  const { refreshKey } = useQuickCreate();

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tasks", { params: { section, ...filters } });
      setTasks(data);
    } finally {
      setLoading(false);
    }
  }, [section, filters, refreshKey]);

  const loadFiles = useCallback(async () => {
    setFilesLoading(true);
    try {
      const { data: status } = await api.get("/drive/status");
      setDriveReady(status.ready);
      if (status.ready) {
        const { data } = await api.get("/drive/files", { params: { section } });
        setFiles(data);
      } else {
        setFiles([]);
      }
    } catch (e) {
      setFiles([]);
    } finally {
      setFilesLoading(false);
    }
  }, [section]);

  useEffect(() => { loadTasks(); }, [loadTasks]);
  useEffect(() => { loadFiles(); }, [loadFiles]);

  const createFromFile = (file) => {
    if (file.already_saved) {
      toast.warning("Bu evrak için zaten bir kayıt var.");
      return;
    }
    setInitial({
      section,
      record_type: "Gelen Evrak",
      title: file.name,
      drive_file_id: file.id,
      drive_link: file.webViewLink,
    });
    setFormOpen(true);
  };

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-foreground">{section}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {section === "Adliye"
              ? "Adli mercilerden gelen evraklar ve açılacak işler."
              : section === "TÜRKPATENT"
                ? "Marka, tasarım, patent ve faydalı model işleri."
                : "Adliye veya TÜRKPATENT kapsamına girmeyen genel evraklar ve işler."}
          </p>
        </div>
        <Button
          onClick={() => { setInitial({ section }); setFormOpen(true); }}
          data-testid="section-create-btn"
          className="bg-crimson text-white hover:bg-crimson/90"
        >
          <Plus className="h-4 w-4" /> Yeni Kayıt
        </Button>
      </div>

      <Tabs defaultValue="isler">
        <TabsList className="rounded-xl" data-testid="section-tabs">
          <TabsTrigger value="isler" className="rounded-lg" data-testid="tab-tasks">Kayıtlar ({tasks.length})</TabsTrigger>
          <TabsTrigger value="drive" className="rounded-lg" data-testid="tab-drive">Gelen Evraklar ({files.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="isler" className="mt-5 space-y-4">
          <Filters filters={filters} setFilters={setFilters} showSection={false} />
          <TaskList
            tasks={tasks}
            loading={loading}
            onEdit={(t) => { setInitial(t); setFormOpen(true); }}
            onComplete={setCompleting}
            onRefresh={loadTasks}
          />
        </TabsContent>

        <TabsContent value="drive" className="mt-5 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-[0.6px] text-muted-foreground">
              {section === "Adliye"
                ? "01 - Adliye Gelen Evraklar"
                : section === "TÜRKPATENT"
                  ? "02 - TÜRKPATENT Gelen Evraklar"
                  : "04 - Diğer Gelen Evraklar"}
            </p>
            <Button variant="outline" size="sm" onClick={loadFiles} data-testid="drive-refresh-btn">
              <RefreshCw className={cn("h-4 w-4", filesLoading && "animate-spin")} /> Yenile
            </Button>
          </div>

          {!driveReady && !filesLoading && (
            <div className="rounded-xl border border-border bg-card p-8 text-center shadow-shb" data-testid="drive-not-ready">
              <FolderOpen className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">
                Google Drive bağlantısı veya klasör yapısı hazır değil. Ayarlar bölümünden bağlayın.
              </p>
            </div>
          )}

          {driveReady && !files.length && !filesLoading && (
            <div className="rounded-xl border border-border bg-card p-8 text-center shadow-shb" data-testid="drive-empty">
              <p className="text-sm text-muted-foreground">Bu klasörde yeni evrak bulunmuyor.</p>
            </div>
          )}

          <div className="space-y-3">
            {files.map((f) => (
              <div
                key={f.id}
                data-testid={`drive-file-${f.id}`}
                className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 shadow-shb sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <p className="truncate font-semibold text-foreground">{f.name}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span className="font-mono">{formatDate(f.modifiedTime)}</span>
                    {f.already_saved && (
                      <Pill className="border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300">
                        Kayıt oluşturuldu
                      </Pill>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openDocument({ fileId: f.id })}
                    data-testid={`file-view-${f.id}`}
                  >
                    <Eye className="h-4 w-4" /> Evrakı Görüntüle
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => createFromFile(f)}
                    data-testid={`file-create-record-${f.id}`}
                    className="bg-navy text-white hover:bg-navy/90 dark:bg-slate-100 dark:text-navy"
                  >
                    <FilePlus2 className="h-4 w-4" /> Evrak kaydı oluştur
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <TaskFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        initial={initial}
        onSaved={() => { loadTasks(); loadFiles(); }}
      />
      <CompleteDialog
        task={completing}
        open={!!completing}
        onOpenChange={(v) => !v && setCompleting(null)}
        onDone={() => { loadTasks(); loadFiles(); }}
      />
    </div>
  );
}
